// A canonical state, shown as the v3 badge.
//
// Every state in the UI goes through this component, so the rule in states.ts —
// no label claims an effect that is not evidenced — is enforced by construction
// rather than by review of each screen.

import type { ReactNode } from 'react';
import { useStates, type StateDisplay } from '../states.ts';

export type StateBadgeProps =
  | {
      readonly table: Readonly<Record<string, StateDisplay>>;
      readonly state: string;
      readonly withDot?: boolean;
      /** Use the state's short label where it has one (narrow rows). */
      readonly short?: boolean;
    }
  | {
      /** Already resolved, e.g. by documentDisplay. */
      readonly shown: StateDisplay;
      readonly withDot?: boolean;
    };

export function StateBadge(props: StateBadgeProps): ReactNode {
  // In the reader's language: the same entry, label and explanation translated,
  // tone and claim untouched. A `shown` from documentDisplay is traced back to
  // its table, so it is translated too.
  const states = useStates();
  const shown = 'shown' in props ? states.shown(props.shown) : states.display(props.table, props.state);
  const withDot = props.withDot ?? false;
  const short = 'short' in props && props.short === true && shown.short !== undefined;
  return (
    <span
      className={`badge ${shown.tone === 'neutral' ? '' : shown.tone}`}
      data-testid="state-badge"
      title={short ? `${shown.label}. ${shown.explanation}` : shown.explanation}
    >
      {withDot ? <span className="dot" /> : null}
      {short ? shown.short : shown.label}
    </span>
  );
}
